import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Clock, History, User } from 'lucide-react';
import { api } from '../../utils/api';

const toMillis = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    return value < 1e12 ? value * 1000 : value;
  }

  if (typeof value === 'string') {
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber)) {
      return asNumber < 1e12 ? asNumber * 1000 : asNumber;
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const formatDateTime = (value) => {
  const ms = toMillis(value);
  if (!ms) return { date: 'N/A', time: 'N/A' };
  try {
    const d = new Date(ms);
    return {
      date: d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }),
      time: d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    };
  } catch {
    return { date: 'N/A', time: 'N/A' };
  }
};

const DateTimeCell = ({ value }) => {
  const { date, time } = formatDateTime(value);
  if (date === 'N/A') return <span className="text-gray-400">N/A</span>;
  return (
    <div>
      <p className="text-sm font-medium text-gray-900">{date}</p>
      <p className="text-xs text-gray-500">{time}</p>
    </div>
  );
};

const getFirstAvailable = (record, keys) => {
  for (const key of keys) {
    if (record?.[key] !== undefined && record?.[key] !== null && record?.[key] !== '') {
      return record[key];
    }
  }
  return null;
};

const formatHours = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `${num.toFixed(2)} hrs`;
};

const normalizeMonth = (value) => {
  if (!value) return 'N/A';

  if (typeof value === 'string') {
    // Expected format from API: YYYY-MM
    if (/^\d{4}-\d{2}$/.test(value)) return value;
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      const d = new Date(parsed);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
    return value;
  }

  const ms = toMillis(value);
  if (!ms) return 'N/A';
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const isLogOnDate = (log, dateStr) => {
  if (!dateStr) return true;

  const dayStart = new Date(`${dateStr}T00:00:00`).getTime();
  const dayEnd = new Date(`${dateStr}T23:59:59.999`).getTime();

  const fromValue = getFirstAvailable(log, ['from', 'fromTime']);
  const toValue = getFirstAvailable(log, ['to', 'toTime']);
  const fromMs = toMillis(fromValue);
  const toMs = toMillis(toValue);

  if (fromMs && toMs) {
    // Keep logs that overlap with the selected date window.
    return fromMs <= dayEnd && toMs >= dayStart;
  }

  const point = fromMs || toMs;
  if (!point) return false;
  return point >= dayStart && point <= dayEnd;
};

const getOverlapMsForDate = (log, dateStr) => {
  if (!dateStr) return 0;

  const dayStart = new Date(`${dateStr}T00:00:00`).getTime();
  const dayEnd = new Date(`${dateStr}T23:59:59.999`).getTime();

  const fromValue = getFirstAvailable(log, ['from', 'fromTime']);
  const toValue = getFirstAvailable(log, ['to', 'toTime']);
  const fromMs = toMillis(fromValue);
  const toMs = toMillis(toValue);

  if (!fromMs || !toMs) return 0;

  const overlapStart = Math.max(fromMs, dayStart);
  const overlapEnd = Math.min(toMs, dayEnd);
  if (overlapEnd <= overlapStart) return 0;

  return overlapEnd - overlapStart;
};

const formatDurationFromMs = (ms) => {
  if (!ms || ms <= 0) return '0h 0m';
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};

const getLogDurationMs = (log) => {
  const fromValue = getFirstAvailable(log, ['from', 'fromTime']);
  const toValue = getFirstAvailable(log, ['to', 'toTime']);
  const fromMs = toMillis(fromValue);
  const toMs = toMillis(toValue);

  if (!fromMs || !toMs || toMs <= fromMs) return 0;
  return toMs - fromMs;
};

const DriverWorkHistoryDetails = () => {
  const { id } = useParams();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [isStatusLogModalOpen, setIsStatusLogModalOpen] = useState(false);
  const [selectedRecordForLogs, setSelectedRecordForLogs] = useState(null);
  const [selectedLogDate, setSelectedLogDate] = useState('');
  const [selectedLogStatus, setSelectedLogStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const {
    data: driverData,
    isLoading: isLoadingDriver,
    error: driverError
  } = useQuery(
    ['driver-work-history-driver-details', id],
    async () => {
      const response = await api.get(`/drivers/${id}`);
      return response?.data?.data?.driver || response?.data?.data || null;
    },
    {
      enabled: Boolean(id),
      retry: false,
      refetchOnWindowFocus: false
    }
  );

  const {
    data: historyData,
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory,
    error: historyError
  } = useQuery(
    ['driver-work-history-details', id, selectedMonth],
    async () => {
      const params = {};
      if (selectedMonth) {
        params.month = selectedMonth;
      }
      const response = await api.get(`/drivers/${id}/workingHours`, { params });
      return response?.data?.data || [];
    },
    {
      enabled: Boolean(id),
      retry: false,
      refetchOnWindowFocus: false
    }
  );

  const records = useMemo(() => {
    const items = Array.isArray(historyData) ? historyData : [];
    const monthlyRows = [];

    items.forEach((doc, docIndex) => {
      const rows = Array.isArray(doc?.workingHours) ? doc.workingHours : [];
      rows.forEach((entry, entryIndex) => {
        // Format A (monthly): { month, totalHours, statusLog: [...] }
        if (entry?.month) {
          const statusLog = Array.isArray(entry.statusLog) ? entry.statusLog : [];
          const firstFrom = statusLog.length
            ? getFirstAvailable(statusLog[0], ['from', 'fromTime'])
            : getFirstAvailable(entry, ['from', 'fromTime']);
          const lastTo = statusLog.length
            ? getFirstAvailable(statusLog[statusLog.length - 1], ['to', 'toTime'])
            : getFirstAvailable(entry, ['to', 'toTime']);

          monthlyRows.push({
            key: `${String(doc?._id || docIndex)}-${entryIndex}`,
            month: normalizeMonth(entry.month),
            totalHours: entry.totalHours,
            statusLog,
            statusLogCount: statusLog.length,
            firstFrom,
            lastTo,
            raw: entry
          });
          return;
        }

        // Format B (status range list): { status, from, to } -> derive month from from/to
        const fromValue = getFirstAvailable(entry, ['from', 'fromTime']);
        const toValue = getFirstAvailable(entry, ['to', 'toTime']);
        const derivedMonth = normalizeMonth(fromValue || toValue);

        monthlyRows.push({
          key: `${String(doc?._id || docIndex)}-${entryIndex}`,
          month: derivedMonth,
          totalHours: null,
          statusLog: [],
          statusLogCount: null,
          firstFrom: fromValue,
          lastTo: toValue,
          raw: entry
        });
      });
    });

    return monthlyRows;
  }, [historyData]);

  const logsForSelectedDate = useMemo(() => {
    const logs = Array.isArray(selectedRecordForLogs?.statusLog)
      ? selectedRecordForLogs.statusLog
      : [];

    return logs.filter((log) => isLogOnDate(log, selectedLogDate));
  }, [selectedRecordForLogs, selectedLogDate]);

  const filteredModalLogs = useMemo(() => {
    return logsForSelectedDate.filter((log) => {
      const status = String(log?.status || '').toLowerCase();
      return selectedLogStatus === 'all' ? true : status === selectedLogStatus;
    });
  }, [logsForSelectedDate, selectedLogStatus]);

  const selectedDateTotalMs = useMemo(() => {
    if (!selectedLogDate) return 0;
    return filteredModalLogs.reduce((sum, log) => sum + getOverlapMsForDate(log, selectedLogDate), 0);
  }, [filteredModalLogs, selectedLogDate]);

  const statusTotals = useMemo(() => {
    return logsForSelectedDate.reduce(
      (acc, log) => {
        const durationMs = selectedLogDate ? getOverlapMsForDate(log, selectedLogDate) : getLogDurationMs(log);
        const status = String(log?.status || '').toLowerCase();

        if (status === 'online') {
          acc.onlineMs += durationMs;
        } else if (status === 'offline') {
          acc.offlineMs += durationMs;
        }

        return acc;
      },
      { onlineMs: 0, offlineMs: 0 }
    );
  }, [logsForSelectedDate, selectedLogDate]);

  const openStatusLogModal = (record) => {
    setSelectedRecordForLogs(record);
    setSelectedLogDate('');
    setSelectedLogStatus('all');
    setIsStatusLogModalOpen(true);
  };

  const closeStatusLogModal = () => {
    setIsStatusLogModalOpen(false);
    setSelectedRecordForLogs(null);
    setSelectedLogDate('');
    setSelectedLogStatus('all');
  };

  React.useEffect(() => {
    if (driverError) {
      toast.error(driverError?.response?.data?.message || 'Failed to fetch driver details');
    }
  }, [driverError]);

  React.useEffect(() => {
    if (historyError) {
      if (historyError?.response?.status === 404) return;
      toast.error(historyError?.response?.data?.message || 'Failed to fetch work history');
    }
  }, [historyError]);

  const driverName = driverData?.name || 'Driver';

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Driver Work History Details</h1>
              <p className="page-subtitle">Detailed working hour history for selected driver</p>
            </div>
            <Link to="/drivers/driverworkhistory" className="btn btn-outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back To Driver List
            </Link>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {isLoadingDriver ? (
            <div className="flex items-center justify-center py-6">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500">Driver Name</p>
                <p className="text-base font-semibold text-gray-900 flex items-center">
                  <User className="h-4 w-4 mr-2 text-gray-500" />
                  {driverName}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Driver ID</p>
                <p className="text-sm text-gray-900 break-all">{id}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm text-gray-900">{driverData?.phone || 'N/A'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Month Filter</h3>
        </div>
        <div className="card-body">
          <div className="max-w-xs">
            <label className="form-label">Month</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => { setSelectedMonth(e.target.value); setCurrentPage(1); }}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-2">
              Data is requested from API with month filter, so only selected month is loaded.
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Work History Records ({records.length})</h3>
        </div>
        <div className="card-body">
          {isLoadingHistory || isFetchingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : records.length === 0 ? (
            <div className="empty-state">
              <History className="empty-state-icon" />
              <h3 className="empty-state-title">No work history records</h3>
              <p className="empty-state-description">No work history entries are available for this driver.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="table w-full" style={{ minWidth: '700px', borderCollapse: 'collapse' }}>
                  <thead className="table-header bg-gray-50">
                    <tr>
                      <th className="table-header-cell text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '12%' }}>Month</th>
                      <th className="table-header-cell text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '14%' }}>Total Hours</th>
                      <th className="table-header-cell text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '14%' }}>Status Logs</th>
                      <th className="table-header-cell text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '22%' }}>
                        From
                        <span className="block text-gray-400 font-normal normal-case tracking-normal">Date &amp; Time</span>
                      </th>
                      <th className="table-header-cell text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '22%' }}>
                        To
                        <span className="block text-gray-400 font-normal normal-case tracking-normal">Date &amp; Time</span>
                      </th>
                      <th className="table-header-cell text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '16%' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="table-body divide-y divide-gray-100">
                    {records.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((record) => (
                      <tr key={record.key} className="table-row hover:bg-gray-50">
                        <td className="table-cell px-4 py-3 text-sm whitespace-nowrap">
                          <span className="font-medium text-gray-900">{record.month}</span>
                        </td>
                        <td className="table-cell px-4 py-3 text-sm whitespace-nowrap">{formatHours(record.totalHours)}</td>
                        <td className="table-cell px-4 py-3 text-sm whitespace-nowrap">
                          <div className="flex items-center text-gray-800">
                            <Clock className="h-3.5 w-3.5 mr-2 text-gray-500 flex-shrink-0" />
                            {record.statusLogCount ?? 'N/A'}
                          </div>
                        </td>
                        <td className="table-cell px-4 py-3"><DateTimeCell value={record.firstFrom} /></td>
                        <td className="table-cell px-4 py-3"><DateTimeCell value={record.lastTo} /></td>
                        <td className="table-cell px-4 py-3 text-sm whitespace-nowrap">
                          {record.statusLogCount && record.statusLogCount > 0 ? (
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onClick={() => openStatusLogModal(record)}
                            >
                              View Status Logs
                            </button>
                          ) : (
                            <span className="text-gray-500">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {records.length > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 px-1">
                  <p className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, records.length)} of {records.length} records
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.ceil(records.length / PAGE_SIZE) }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        className={`btn btn-sm ${
                          page === currentPage ? 'btn-primary' : 'btn-outline'
                        }`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setCurrentPage((p) => Math.min(Math.ceil(records.length / PAGE_SIZE), p + 1))}
                      disabled={currentPage === Math.ceil(records.length / PAGE_SIZE)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isStatusLogModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeStatusLogModal}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              x
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              Status Logs - {selectedRecordForLogs?.month || 'N/A'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-xs text-green-700">Total Online Hours</p>
                <p className="text-lg font-semibold text-green-800">
                  {formatDurationFromMs(statusTotals.onlineMs)}
                </p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-xs text-red-700">Total Offline Hours</p>
                <p className="text-lg font-semibold text-red-800">
                  {formatDurationFromMs(statusTotals.offlineMs)}
                </p>
              </div>
            </div>

            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label className="form-label">Filter By Date</label>
                <input
                  type="date"
                  className="input"
                  value={selectedLogDate}
                  onChange={(e) => setSelectedLogDate(e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">Filter By Status</label>
                <select
                  className="input"
                  value={selectedLogStatus}
                  onChange={(e) => setSelectedLogStatus(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Showing {filteredModalLogs.length} of {selectedRecordForLogs?.statusLogCount || 0} logs
              </p>
              {selectedLogDate ? (
                <p className="text-sm font-medium text-gray-800 mt-1">
                  Total Hours For {selectedLogDate}: {formatDurationFromMs(selectedDateTotalMs)}
                </p>
              ) : null}
            </div>

            {filteredModalLogs.length === 0 ? (
              <div className="empty-state">
                <History className="empty-state-icon" />
                <h3 className="empty-state-title">No status logs found</h3>
                <p className="empty-state-description">Try another date or clear the date filter.</p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Status</th>
                      <th className="table-header-cell">
                        From
                        <span className="block text-gray-400 font-normal normal-case tracking-normal">Date &amp; Time</span>
                      </th>
                      <th className="table-header-cell">
                        To
                        <span className="block text-gray-400 font-normal normal-case tracking-normal">Date &amp; Time</span>
                      </th>
                      <th className="table-header-cell">Working Hours</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filteredModalLogs.map((log, idx) => (
                      <tr
                        key={`${selectedRecordForLogs?.key || 'log'}-${idx}`}
                        className={`table-row align-top ${String(log?.status || '').toLowerCase() === 'online' ? 'bg-green-50' : ''} ${String(log?.status || '').toLowerCase() === 'offline' ? 'bg-red-50' : ''}`}
                      >
                        <td className="table-cell">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${String(log?.status || '').toLowerCase() === 'online' ? 'bg-green-100 text-green-800' : ''} ${String(log?.status || '').toLowerCase() === 'offline' ? 'bg-red-100 text-red-800' : ''}`}
                          >
                            {String(log?.status || 'N/A')}
                          </span>
                        </td>
                        <td className="table-cell"><DateTimeCell value={getFirstAvailable(log, ['from', 'fromTime'])} /></td>
                        <td className="table-cell"><DateTimeCell value={getFirstAvailable(log, ['to', 'toTime'])} /></td>
                        <td className="table-cell">
                          <span className="font-medium text-gray-900">
                            {formatDurationFromMs(getLogDurationMs(log))}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverWorkHistoryDetails;
